import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { MapPin, ArrowRight, Sparkles, ArrowLeft } from "lucide-react";

export const Destinations = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { DESTINATIONS, WHY_KERALA_POINTS } = siteData;
    usePageMeta(
        "Destinations",
        "UniversHeal coordinates healthcare across India — starting in Kerala, God's Own Country, and built to grow into India's leading healthcare cities."
    );
    const live = DESTINATIONS.filter((d) => d.status === "live");
    const upcoming = DESTINATIONS.filter((d) => d.status !== "live");

    return (
        <>
            <PageHero
                eyebrow={t("destinations.eyebrow", "Destinations")}
                title={
                    <>
                        Healthcare across India,{" "}
                        <span className="italic text-pine">starting in Kerala.</span>
                    </>
                }
                intro={t("destinations.intro", "UniversHeal begins in Kerala — God's Own Country — and is built to grow with you across India's leading healthcare cities. Today, we coordinate care in Kerala.")}
            />

            <section className="pb-8">
                <div className="container-site grid lg:grid-cols-2 gap-6">
                    {live.map((d) => (
                        <Reveal key={d.slug} className="h-full">
                            <Link
                                to={`/destinations/${d.slug}`}
                                data-testid={`destination-card-${d.slug}`}
                                className="group relative block h-[380px] sm:h-[460px] rounded-2xl overflow-hidden card-lift"
                            >
                                <img
                                    src={IMG[d.img]}
                                    alt={d.imgAlt}
                                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    aria-hidden="true"
                                    className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/20 to-transparent"
                                />
                                <div className="absolute bottom-0 inset-x-0 p-8 sm:p-10 text-ivory">
                                    <span
                                        data-testid={`destination-chip-${d.slug}`}
                                        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest2 bg-ivory/95 text-forest px-3 py-1.5 rounded-md"
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        {d.tagline}
                                    </span>
                                    <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl">
                                        {d.name}
                                    </h2>
                                    <p className="mt-2 font-serif italic text-lg text-ivory/85">
                                        {t("destinations.text1", "God's Own Country")}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold">
                                        {t("destinations.text2", "Explore Kerala")}
                                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}

                    <Reveal delay={0.1} className="h-full">
                        <div className="h-full bg-white border border-pine/10 rounded-2xl p-8 sm:p-10 flex flex-col justify-center">
                            <LeafMark className="w-8 h-8 text-natural" />
                            <h2 className="mt-6 font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight">
                                Built to grow{" "}
                                <span className="italic text-pine">across India.</span>
                            </h2>
                            <p className="mt-5 text-sm sm:text-[15px] leading-relaxed text-moss max-w-md">
                                {t("destinations.text3", "Our coordination model is designed for India's leading healthcare cities. As each new destination launches, it will be announced here.")}
                            </p>
                            <ul className="mt-7 flex flex-wrap gap-2.5" data-testid="destinations-upcoming">
                                {upcoming.map((d) => (
                                    <li
                                        key={d.slug}
                                        data-testid={`destination-upcoming-${d.slug}`}
                                        className="inline-flex items-center gap-2 text-sm font-semibold text-moss bg-sage/70 border border-pine/10 rounded-lg px-3.5 py-2"
                                    >
                                        <MapPin className="w-3.5 h-3.5 text-natural" />
                                        {d.name}
                                        <span className="text-[10px] uppercase tracking-widest text-moss/60">
                                            Soon,
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("destinations.headline", "Wherever we operate, you're never alone.")}
                text={t("destinations.text", "Start with a medical opinion — our coordination team supports you through every step of your healthcare journey in India.")}
            />
        </>
    );
};

const UnknownDestination = () => {
    const { t } = useLang();
    return (
    <div className="pt-40 pb-32 text-center container-site">
        <p className="eyebrow text-moss">{t("destination.unknown.eyebrow", "Destination not found")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-5 text-forest">
            {t("destination.unknown.title", "We couldn't find that destination.")}
        </h1>
        <Link
            to="/destinations"
            data-testid="destination-unknown-back"
            className="btn-primary mt-10 inline-flex"
        >
            {t("destination.unknown.back", "All Destinations")}
        </Link>
    </div>
    );
};

export const DestinationDetail = () => {
    const { slug } = useParams();
    const { t, tHtml, site: siteData } = useLang();
    const { DESTINATIONS, WHY_KERALA_POINTS } = siteData;
    const d = DESTINATIONS.find((x) => x.slug === slug);
    usePageMeta(
        d ? `${d.name} ${t("destination.metaSuffix", "— Destination")}` : t("destination.metaTitle", "Destination"),
        d
            ? t("destination.metaDesc", "UniversHeal coordinates healthcare in Kerala, God's Own Country — advanced hospitals, experienced specialists and a naturally beautiful, restorative setting.")
            : t("destination.metaDescGeneric", "Discover UniversHeal destinations across India.")
    );

    if (!d) return <UnknownDestination />;

    if (d.status !== "live") {
        return (
            <div className="pt-40 pb-32 text-center container-site">                <p className="eyebrow text-moss flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4 text-natural" />
                    {t("destination.launchingSoon", "Launching soon")}
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-5 text-forest">
                    {t("destination.roadmap", "{name} is on our roadmap.").replace("{name}", d.name)}
                </h1>
                <p className="mt-5 text-moss max-w-md mx-auto leading-relaxed">
                    {t("destination.roadmapNote", "We coordinate care in Kerala today. As new destinations launch across India, they will be announced here.")}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to="/destinations/kerala"
                        data-testid="destination-soon-kerala"
                        className="btn-primary"
                    >
                        {t("destination.exploreKerala", "Explore Kerala")}
                    </Link>
                    <Link
                        to="/get-a-medical-opinion"
                        data-testid="destination-soon-opinion"
                        className="btn-secondary"
                    >
                        {t("destination.getOpinion", "Get a Medical Opinion")}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <PageHero
                eyebrow={`${d.name} ${t("destination.eyebrowSuffix", "— Our first destination")}`}
                title={tHtml(
                    "destination.title",
                    <>
                        Advanced healthcare in{" "}
                        <span className="italic text-pine">God's Own Country.</span>
                    </>
                )}
                intro={d.intro}
            >
                <Reveal delay={0.2}>
                    <Link
                        to="/destinations"
                        data-testid="destination-back-link"
                        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-moss hover:text-forest transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t("destinations.text5", "All destinations")}
                    </Link>
                </Reveal>
            </PageHero>

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-72 sm:h-[480px] shadow-[0_40px_90px_rgba(6,60,53,0.16)] relative">
                            <img
                                src={IMG[d.img]}
                                alt={d.imgAlt}
                                data-testid="destination-hero-image"
                                className="w-full h-full object-cover"
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent"
                            />
                            <p
                                className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-serif italic text-2xl sm:text-3xl text-ivory drop-shadow-[0_2px_10px_rgba(6,60,53,0.8)]"
                                data-testid="destination-statement"
                            >
                                {t('destination.quote', '"Where advanced medicine meets timeless beauty."')}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-10">
                <div className="container-site">
                    <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight max-w-2xl">
                        {t("destination.offers", "What {name} offers international patients.").replace("{name}", d.name)}{" "}
                        <span className="italic text-pine">{t("destination.offersAccent", "international patients.")}</span>
                    </h2>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {WHY_KERALA_POINTS.map((point, i) => (
                            <Reveal key={point.title} delay={i * 0.05} className="h-full">
                                <div
                                    data-testid={`destination-point-${i + 1}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-7 card-lift"
                                >
                                    <span className="font-serif italic text-xl text-natural">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="mt-3.5 font-serif font-medium text-xl text-forest leading-snug">
                                        {point.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-moss">
                                        {point.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <Link
                            to="/why-kerala"
                            data-testid="destination-why-kerala-link"
                            className="mt-10 inline-flex btn-secondary group"
                        >
                            {t("destination.readKeralaStory", "Read the full Kerala story")}
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("destinations.headline", "Ready to see Kerala for yourself?")}
                text={t("destinations.text", "Tell us about your needs and we will help you understand what care in Kerala could look like for you.")}
            />
        </>
    );
};
